import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Search, Plus, Edit2, Trash2, RotateCcw, Upload, Loader2, X } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useIcd10Cache, useNhrplCache } from '../hooks/useCodeCache';
import { DeleteConfirmModal } from '../components/DeleteConfirmModal';

interface ManagedCode {
  code: string;
  description: string;
  active: boolean;
  chapter?: string;
  block?: string;
  tariffAmount?: number;
  category?: string;
  custom?: boolean;
}

interface CodeManagementPageProps {
  codeType: 'icd10' | 'nhrpl';
  title: string;
  showTariff?: boolean;
  showCategory?: boolean;
  showChapter?: boolean;
}

export function CodeManagementPage({ codeType, title, showTariff, showCategory, showChapter }: CodeManagementPageProps) {
  const { post, postForm, patch, del } = useApi();
  const canWrite = true;

  const icd10Cache = useIcd10Cache();
  const nhrplCache = useNhrplCache();
  const cache = codeType === 'icd10' ? icd10Cache : nhrplCache;

  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [error, setError] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 50;

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [editCode, setEditCode] = useState<ManagedCode | null>(null);
  const [deleteCode, setDeleteCode] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; errors: string[] } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Form state
  const [formCode, setFormCode] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formChapter, setFormChapter] = useState('');
  const [formBlock, setFormBlock] = useState('');
  const [formTariff, setFormTariff] = useState('0');
  const [formCategory, setFormCategory] = useState('');

  // Client-side filtering and pagination from the window cache
  const filtered = useMemo(() => {
    const allCodes = cache.codes as ManagedCode[];
    if (!allCodes.length) return [];
    const q = search.toLowerCase().trim();
    return allCodes.filter(c => {
      if (!showInactive && c.active === false) return false;
      if (q && !c.code.toLowerCase().includes(q) && !c.description.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [cache.codes, search, showInactive]);

  const total = filtered.length;
  const codes = useMemo(() => filtered.slice(offset, offset + limit), [filtered, offset]);
  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  const refreshCache = useCallback(() => {
    cache.invalidate();
  }, [cache]);

  const openAddModal = () => {
    setFormCode('');
    setFormDesc('');
    setFormChapter('');
    setFormBlock('');
    setFormTariff('0');
    setFormCategory('');
    setEditCode(null);
    setShowAddModal(true);
  };

  const openEditModal = (c: ManagedCode) => {
    setFormCode(c.code);
    setFormDesc(c.description);
    setFormChapter(c.chapter || '');
    setFormBlock(c.block || '');
    setFormTariff(String(c.tariffAmount ?? 0));
    setFormCategory(c.category || '');
    setEditCode(c);
    setShowAddModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      if (editCode) {
        const body: Record<string, unknown> = { description: formDesc };
        if (showChapter) { body.chapter = formChapter; body.block = formBlock; }
        if (showTariff) body.tariffAmount = parseFloat(formTariff) || 0;
        if (showCategory) body.category = formCategory;

        const res = await patch(`/${codeType}/manage/${encodeURIComponent(editCode.code)}`, body);
        if (!res.success) { setError(res.error || 'Failed to update'); return; }
      } else {
        const body: Record<string, unknown> = { code: formCode, description: formDesc };
        if (showChapter) { body.chapter = formChapter; body.block = formBlock; }
        if (showTariff) body.tariffAmount = parseFloat(formTariff) || 0;
        if (showCategory) body.category = formCategory;

        const res = await post(`/${codeType}/manage`, body);
        if (!res.success) { setError(res.error || 'Failed to create'); return; }
      }
      setShowAddModal(false);
      refreshCache();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deleteCode) return;
    try {
      const res = await del(`/${codeType}/manage/${encodeURIComponent(deleteCode)}`);
      if (!res.success) throw new Error(res.error || 'Failed to deactivate');
      setDeleteCode(null);
      refreshCache();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        || (err instanceof Error ? err.message : 'Failed to deactivate');
      setError(msg);
      setDeleteCode(null);
    }
  };

  const handleReactivate = async (code: string) => {
    const res = await post(`/${codeType}/manage/${encodeURIComponent(code)}/reactivate`, {});
    if (res.success) refreshCache();
    else setError(res.error || 'Failed to reactivate');
  };

  const handleCsvImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    setError('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await postForm<{ imported: number; errors: string[] }>(`/${codeType}/manage/import-csv`, formData);
      if (res.success && res.data) {
        setImportResult(res.data);
        refreshCache();
      } else {
        setError(res.error || 'Import failed');
      }
    } catch {
      setError('Import failed');
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-900">{title}</h1>
        {canWrite && (
          <div className="flex items-center gap-3">
            <input ref={fileRef} type="file" accept=".csv" onChange={handleCsvImport} className="hidden" />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={importing}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2"
            >
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Import CSV
            </button>
            <button
              onClick={openAddModal}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus className="h-4 w-4" /> Add Code
            </button>
          </div>
        )}
      </div>

      {/* Import result */}
      {importResult && (
        <div className="mb-4 p-3 rounded bg-green-50 border border-green-200 text-green-700 text-sm flex items-center justify-between">
          <span>Imported {importResult.imported} codes. {importResult.errors.length > 0 && `${importResult.errors.length} errors.`}</span>
          <button onClick={() => setImportResult(null)} className="text-green-500 hover:text-green-700"><X className="h-4 w-4" /></button>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      {/* Search + filters */}
      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text" value={search} onChange={e => { setSearch(e.target.value); setOffset(0); }}
            placeholder="Search by code or description..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap">
          <input type="checkbox" checked={showInactive} onChange={e => { setShowInactive(e.target.checked); setOffset(0); }} className="h-4 w-4 text-blue-600 rounded" />
          Show inactive
        </label>
      </div>

      {/* Table */}
      {cache.loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
      ) : codes.length === 0 ? (
        <div className="text-center py-12 text-gray-500">{search ? 'No codes match your search.' : 'No codes found.'}</div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                {showChapter && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Chapter</th>}
                {showTariff && <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Tariff</th>}
                {showCategory && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>}
                {showInactive && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>}
                {canWrite && <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {codes.map(c => (
                <tr key={c.code} className={`${!c.active ? 'opacity-60 bg-gray-50' : 'hover:bg-gray-50'}`}>
                  <td className="px-4 py-3 text-sm font-mono font-medium text-gray-900">{c.code}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 max-w-md truncate">{c.description}</td>
                  {showChapter && <td className="px-4 py-3 text-sm text-gray-500 max-w-xs overflow-hidden text-ellipsis whitespace-nowrap" title={c.chapter || ''}>{c.chapter}</td>}
                  {showTariff && <td className="px-4 py-3 text-sm text-gray-700 text-right">R{(c.tariffAmount ?? 0).toFixed(2)}</td>}
                  {showCategory && <td className="px-4 py-3 text-sm text-gray-500">{c.category}</td>}
                  {showInactive && (
                    <td className="px-4 py-3">
                      {c.active !== false
                        ? <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">Active</span>
                        : <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800">Inactive</span>
                      }
                    </td>
                  )}
                  {canWrite && (
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => openEditModal(c)} className="p-1.5 text-gray-400 hover:text-blue-600" title="Edit">
                          <Edit2 className="h-4 w-4" />
                        </button>
                        {c.active !== false ? (
                          <button onClick={() => setDeleteCode(c.code)} className="p-1.5 text-gray-400 hover:text-red-600" title="Deactivate">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        ) : (
                          <button onClick={() => handleReactivate(c.code)} className="p-1.5 text-gray-400 hover:text-green-600" title="Reactivate">
                            <RotateCcw className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between text-sm text-gray-600">
              <span>Showing {offset + 1}–{Math.min(offset + limit, total)} of {total.toLocaleString()}</span>
              <div className="flex gap-2">
                <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}
                  className="px-3 py-1 border rounded disabled:opacity-40">Prev</button>
                <button disabled={currentPage >= totalPages} onClick={() => setOffset(offset + limit)}
                  className="px-3 py-1 border rounded disabled:opacity-40">Next</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h2 className="text-lg font-bold text-gray-900 mb-4">{editCode ? 'Edit Code' : 'Add New Code'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                <input
                  type="text" value={formCode} onChange={e => setFormCode(e.target.value)}
                  disabled={!!editCode} required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  placeholder="e.g. N80.1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text" value={formDesc} onChange={e => setFormDesc(e.target.value)} required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Description"
                />
              </div>
              {showChapter && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Chapter</label>
                    <input type="text" value={formChapter} onChange={e => setFormChapter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Block</label>
                    <input type="text" value={formBlock} onChange={e => setFormBlock(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
              )}
              {showTariff && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tariff Amount (R)</label>
                  <input type="number" step="0.01" value={formTariff} onChange={e => setFormTariff(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              )}
              {showCategory && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <input type="text" value={formCategory} onChange={e => setFormCategory(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={submitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
                  {submitting ? 'Saving...' : editCode ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteCode && (
        <DeleteConfirmModal
          title="Deactivate Code"
          description={`This will deactivate code "${deleteCode}". It can be reactivated later.`}
          onConfirm={handleDeactivate}
          onCancel={() => setDeleteCode(null)}
        />
      )}
    </main>
  );
}
